import { useState, useEffect, useCallback, useRef } from "react";
import { fetchCards, normalizeFromPPT, fetchSets, cardNumberMatches, fetchCardEbayData, psaPricesFromEbayPayload } from "./index";
import { isOversizeCard } from "./constants";
import { save } from "./stamps";

/* The server caps a page at ten and skips cards it cannot price, so asking for
   twelve never produced twelve — it produced ten or fewer, and a grid whose
   last row was part empty under a See More button. Asking for what can
   actually arrive at least makes the number honest. */
const PAGE_SIZE = 10;
const HIDE_OVERSIZE_KEY = "ppt_hide_oversize_v1";

// Hiding jumbos is a standing preference, not something to re-tick every visit.
function loadHideOversize() {
  try {
    const raw = window.localStorage.getItem(HIDE_OVERSIZE_KEY);
    return raw == null ? true : JSON.parse(raw) === true;   // default: hidden
  } catch {
    return true;
  }
}

// Returns true if card belongs to one of the selected sets (by id or name)
function cardMatchesSelectedSets(card, selectedIds, setsList) {
  if (!card) return false;
  if (!Array.isArray(selectedIds) || selectedIds.length === 0) return true;

  const ids = new Set(selectedIds);
  if (card.setId && ids.has(card.setId)) return true;

  const sets = Array.isArray(setsList) ? setsList : [];
  const selectedNames = new Set(
    sets
      .filter((s) => ids.has(s.id))
      .map((s) => (s.name || "").toLowerCase())
  );

  const setName = (card.setName || "").toLowerCase();
  if (setName && selectedNames.size > 0) {
    for (const name of selectedNames) {
      if (!name) continue;
      if (setName.includes(name) || name.includes(setName)) return true;
    }
  }
  return false;
}

export default function useCardSearch() {
  const [cardName, setCardName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [setFilters, setSetFilters] = useState([]);
  const [availableSets, setAvailableSets] = useState([]);
  const [isNameDirty, setIsNameDirty] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkInput, setBulkInput] = useState("");
  const [bulkErrors, setBulkErrors] = useState([]);
  const [debugInfo, setDebugInfo] = useState(null);
  // PSA price cache: cardId -> { prices: { 9: N, 10: N } | null, loading: bool }
  const [psaPriceCache, setPsaPriceCache] = useState({});
  const [hideOversize, setHideOversize] = useState(loadHideOversize);
  // Only the newest search may write results, errors, pagination, or loading
  // state. A slower earlier request must never replace what the user just
  // asked for.
  const searchRunRef = useRef(0);

  // Fetch eBay graded data for a card and extract PSA prices for all grades
  const loadPsaPricing = useCallback(async (cardId) => {
    if (psaPriceCache[cardId]?.prices || psaPriceCache[cardId]?.loading) return;
    setPsaPriceCache((prev) => ({ ...prev, [cardId]: { loading: true } }));
    try {
      const prices = psaPricesFromEbayPayload(await fetchCardEbayData(cardId));
      setPsaPriceCache((prev) => ({
        ...prev,
        [cardId]: { prices, loading: false },
      }));
    } catch (e) {
      setPsaPriceCache((prev) => ({
        ...prev,
        [cardId]: { prices: null, loading: false, error: e?.response?.data?.message || e.message },
      }));
    }
  }, [psaPriceCache]);

  // Load sets list once on mount
  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchSets({ limit: 500, sortBy: "releaseDate", sortOrder: "desc" });
        const list = Array.isArray(data?.data) ? data.data : [];
        setAvailableSets(list.map((s) => ({ id: s.id, name: s.name })));
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
          console.warn("Failed to load sets list", e);
        }
      }
    };
    load();
  }, []);

  // Ref to keep availableSets accessible in callbacks without stale closures
  const availableSetsRef = useRef(availableSets);
  availableSetsRef.current = availableSets;

  const handleSearch = useCallback(async (currentOffset = 0) => {
    const run = ++searchRunRef.current;
    if (!cardName && !cardNumber) {
      setLoading(false);
      setError("Enter a card name or number.");
      return;
    }
    setLoading(true);
    setError("");
    if (currentOffset === 0) {
      setOffset(0);
    }

    try {
      let effectiveName = cardName;
      let effectiveNumber = cardNumber;
      // Parse trailing number from name field (e.g. "Charizard 199")
      if (!effectiveNumber && effectiveName) {
        const m = effectiveName.match(/^(.*\S)\s+(\d+[A-Za-z]?)$/);
        if (m) {
          effectiveName = m[1];
          effectiveNumber = m[2];
        }
      }

      const params = { limit: PAGE_SIZE, offset: currentOffset };
      if (effectiveName) params.search = effectiveName;
      // No cardNumber here: the API rejects it outright with a 400, which made
      // every search that carried a number fail. The number is applied below,
      // against the results, which is what the rest of this function already did.
      if (Array.isArray(setFilters) && setFilters.length === 1) {
        params.setId = setFilters[0];
      }

      const data = await fetchCards(params);
      if (run !== searchRunRef.current) return;
      const rawData = data?.data || [];
      let normalized = rawData.map(normalizeFromPPT).filter(Boolean);

      // Number-based filtering with fallback broad search
      if (effectiveNumber) {
        normalized = normalized.filter((c) => cardNumberMatches(effectiveNumber, c.number));
        if (normalized.length === 0) {
          // Putting the number in the query ranks the exact card first, so a
          // wide page isn't needed. The plan bills per card returned.
          const fallbackParams = { limit: 20 };
          fallbackParams.search = effectiveName
            ? `${effectiveName} ${effectiveNumber}`
            : String(effectiveNumber);
          const fallbackData = await fetchCards(fallbackParams);
          if (run !== searchRunRef.current) return;
          normalized = (fallbackData?.data || [])
            .map(normalizeFromPPT)
            .filter(Boolean)
            .filter((c) => cardNumberMatches(effectiveNumber, c.number));
        }
      }

      // Fallback: broaden search when set filter yields too few results
      const sets = availableSetsRef.current;
      const hasSelectedSets = Array.isArray(setFilters) && setFilters.length > 0;
      const filteredCountForSelectedSets = hasSelectedSets
        ? normalized.filter((c) => cardMatchesSelectedSets(c, setFilters, sets)).length
        : normalized.length;
      // Only worth broadening when several sets are selected. With exactly one,
      // the first query already filtered by setId upstream, so a thin result
      // means the set really does hold few matches — and this fallback would
      // spend 120 credits (more than a day's free allowance) to prove it.
      const canFilterUpstream = hasSelectedSets && setFilters.length === 1;
      if (!effectiveNumber && hasSelectedSets && !canFilterUpstream && filteredCountForSelectedSets < 6) {
        const fallbackData = await fetchCards({ search: effectiveName, limit: 60 });
        if (run !== searchRunRef.current) return;
        const fallbackCards = (fallbackData?.data || []).map(normalizeFromPPT).filter(Boolean);
        const filteredFallback = fallbackCards.filter((c) =>
          cardMatchesSelectedSets(c, setFilters, sets)
        );
        // Merge & dedupe
        const seen = new Set();
        normalized = [...normalized, ...filteredFallback].filter((c) => {
          if (!c || seen.has(c.id)) return false;
          seen.add(c.id);
          return true;
        });
      }

      // Capture debug info
      try {
        const setCounts = normalized.reduce((acc, c) => {
          const key = c.setId || "(null)";
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        }, {});
        const numberCounts = normalized.reduce((acc, c) => {
          const key = c.number || "(null)";
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        }, {});
        setDebugInfo({
          when: new Date().toISOString(),
          requestParams: params,
          effectiveName,
          effectiveNumber,
          setFilters,
          pageOffsetRequested: currentOffset,
          rawCount: rawData.length,
          normalizedCount: normalized.length,
          setCounts,
          numberCounts,
          meta: data?.metadata || {},
          sample: normalized.slice(0, 5),
        });
      } catch { /* non-fatal */ }

      // Merge results into state
      setSearchResults((prev) =>
        currentOffset === 0
          ? normalized
          : [...prev, ...normalized.filter((c) => !prev.some((p) => p.id === c.id))]
      );

      // The server skips cards it can't price, so it reports where the next
      // page starts. Counting the returned cards here would re-show the
      // skipped ones on See More.
      const nextOffset = Number(data.metadata?.nextOffset);
      const pageCount = rawData.length;
      setOffset((prevOffset) =>
        Number.isFinite(nextOffset) ? nextOffset : prevOffset + pageCount
      );

      setHasMore(!!data.metadata?.hasMore && !effectiveNumber);

      if (normalized.length === 0 && currentOffset === 0) {
        setError("No cards found. Refine your search.");
      }
    } catch (err) {
      if (run !== searchRunRef.current) return;
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err?.message || "Unknown error";
      console.error("Search error:", status, message, err?.response?.data || err);
      if (status === 429) setError("Rate limit exceeded. Wait 60 seconds.");
      else setError(`Error fetching data${status ? ` (${status})` : ""}: ${message}`);
    } finally {
      if (run === searchRunRef.current) setLoading(false);
    }
  }, [cardName, cardNumber, setFilters]);

  // Auto-search when set filter changes (if name hasn't been edited since last search)
  useEffect(() => {
    if (!isNameDirty && (cardName || cardNumber)) {
      handleSearch(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFilters]);

  const handleBulkAdd = useCallback(async (addToCollection) => {
    const lines = bulkInput.split("\n").map((l) => l.trim()).filter(Boolean);
    let errors = [];
    let added = [];

    for (const line of lines) {
      const match = line.match(/(.+)\s+(\d+)$/);
      if (!match) {
        errors.push(`${line} — needs a name then a number`);
        continue;
      }
      const [, name, number] = match;
      try {
        // Same trick the single lookup uses: the number in the query ranks the
        // exact print first, so 10 results beat paging through 50.
        const data = await fetchCards({ search: `${name} ${number}`, limit: 10 });
        const normalized = (data?.data || [])
          .map(normalizeFromPPT)
          .filter(Boolean)
          .filter((c) => cardNumberMatches(number, c.number));
        if (!normalized.length) {
          errors.push(`${line} — no card with that number`);
        } else {
          // Only the exact print, not every card the search happened to return.
          added.push(normalized[0]);
        }
      } catch (e) {
        // A swallowed error made a rate limit, a dead proxy and a genuine miss
        // all read as "could not find", which is why three real cards looked
        // like they didn't exist.
        const status = e?.response?.status;
        errors.push(
          status === 429
            ? `${line} — out of API credits (resets 00:00 UTC)`
            : `${line} — lookup failed${status ? ` (${status})` : ""}`
        );
      }
    }

    added.forEach(addToCollection);
    setBulkErrors(errors);
    setShowBulk(false);
    setBulkInput("");
  }, [bulkInput]);

  // Derive filtered results by selected sets
  const bySet =
    Array.isArray(setFilters) && setFilters.length > 0
      ? searchResults.filter((c) => cardMatchesSelectedSets(c, setFilters, availableSets))
      : searchResults;

  // Jumbo promos crowd out the normal print you're usually looking for, so they
  // are hidden by default. Nothing disappears silently — the count of what's
  // hidden is shown next to the toggle.
  const oversizeHiddenCount = hideOversize ? bySet.filter(isOversizeCard).length : 0;
  const filteredResults = hideOversize ? bySet.filter((c) => !isOversizeCard(c)) : bySet;

  return {
    hideOversize,
    setHideOversize: (v) => {
      setHideOversize(v);
      try {
        save(HIDE_OVERSIZE_KEY, JSON.stringify(!!v));
      } catch {
        /* a blocked localStorage shouldn't break the toggle */
      }
    },
    oversizeHiddenCount,
    cardName,
    setCardName: (v) => { setCardName(v); setIsNameDirty(true); },
    cardNumber,
    setCardNumber,
    searchResults: filteredResults,
    rawSearchResults: searchResults,
    loading,
    error,
    hasMore,
    showBulk,
    setShowBulk,
    bulkInput,
    setBulkInput,
    bulkErrors,
    debugInfo,
    availableSets,
    setFilters,
    setSetFilters,
    psaPriceCache,
    loadPsaPricing,
    handleSearch: () => { setIsNameDirty(false); handleSearch(0); },
    handleSeeMore: () => handleSearch(offset),
    handleBulkAdd,
  };
}
